import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Cookie, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const consent = {
      necessary: true,
      analytics: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consent));
    setIsVisible(false);
    setShowSettings(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto">
        <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl shadow-black/50 p-6">
          {!showSettings ? (
            <>
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-12 h-12 rounded-full bg-indigo-600/20 items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-indigo-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold text-lg mb-2">
                    Utilizamos cookies
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-4">
                    Usamos cookies técnicas necesarias para el funcionamiento del sitio y cookies de análisis 
                    para mejorar tu experiencia. Puedes aceptar todas, rechazarlas o configurar tus preferencias.{" "}
                    <Link to="/politica-cookies" className="text-indigo-400 hover:text-indigo-300 underline">
                      Más información
                    </Link>
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={handleAcceptAll}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-6"
                      data-testid="cookie-accept-all"
                    >
                      Aceptar todas
                    </Button>
                    <Button
                      onClick={handleRejectAll}
                      variant="outline"
                      className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-6"
                      data-testid="cookie-reject-all"
                    >
                      Rechazar
                    </Button>
                    <Button
                      onClick={() => setShowSettings(true)}
                      variant="ghost"
                      className="text-slate-400 hover:text-white hover:bg-slate-800"
                      data-testid="cookie-settings"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Configurar
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5 text-indigo-400" />
                  Configurar cookies
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Cookies necesarias</p>
                    <p className="text-slate-400 text-sm">
                      Esenciales para el funcionamiento básico del sitio web.
                    </p>
                  </div>
                  <div className="bg-indigo-600 text-white text-xs px-3 py-1 rounded-full">
                    Siempre activas
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Cookies de análisis</p>
                    <p className="text-slate-400 text-sm">
                      Nos ayudan a entender cómo interactúas con el sitio.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={handleSavePreferences}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6"
                >
                  Guardar preferencias
                </Button>
                <Button
                  onClick={() => setShowSettings(false)}
                  variant="ghost"
                  className="text-slate-400 hover:text-white hover:bg-slate-800"
                >
                  Cancelar
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
